import React, { useState } from 'react';
import '../../styles/Auth.css';
import apiService from '../../apiService';

const SignUpModal = ({ isOpen, onClose, onBackToLogin,authStep,setAuthStep,isAuthenticated,setIsAuthenticated,socialMediaLoginEnabled,onShowAbout,onRegistrationSuccess}) => {
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    password_confirmation: '',
    terms: false
  });
 // setIsAuthenticated(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [mobileAlreadyExists, setMobileAlreadyExists] = useState(false);  

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Please enter your full name';
        if (value.length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          return 'Name can only contain letters, spaces, hyphens and apostrophes';
        }
        return '';

      case 'mobile':
        if (!value.trim()) return 'Please enter your mobile number';
        if (!/^\+?[1-9]\d{1,14}$/.test(value)) {
          return 'Please enter a valid mobile number with country code';
        }
        return '';

      case 'email':
        if (!value.trim()) return 'Please enter your email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        }
        return '';

      case 'password':
        if (!value) return 'Please enter a password';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])(?=.*[A-Z])/.test(value)) {
          return 'Password must contain both uppercase and lowercase letters';
        }
        if (!/(?=.*\d)/.test(value)) {
          return 'Password must contain at least one number';
        }
        return '';

      case 'password_confirmation':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';

      case 'terms':
        if (!value) return 'You must agree to the terms and conditions';
        return '';

      default:
        return '';
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));

    if (name === 'mobile') setMobileAlreadyExists(false);

    // Calculate password strength
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear error when user starts typing
    if (touched[name]) {
      const error = validateField(name, fieldValue);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    const error = validateField(name, fieldValue);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    setTouched({
      name: true,
      mobile: true,
      email: true,
      password: true,
      password_confirmation: true,
      terms: true
    });

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      // Make API call to Laravel backend
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        // Success
        setSuccessMessage('Account created successfully! Redirecting...');
        
        // Store token if provided
        if (data.token) {
          apiService.setToken(data.token);
        }

        if (data.user) {
          apiService.setUser(data.user);
        }

        // Reset form
        setFormData({
          name: '',
          mobile: '',
          email: '',
          password: '',
          password_confirmation: '',
          terms: false
        });
        setPasswordStrength(0);
        setMobileAlreadyExists(false);

        if (onRegistrationSuccess) {
          onRegistrationSuccess(data.user);
        } else {
          setTimeout(() => { window.location.href = '/'; }, 2000);
        }

      } else {
        // Handle validation errors from backend
        if (data.errors) {
          setErrors(data.errors);
          const mobileErr = Array.isArray(data.errors.mobile) ? data.errors.mobile[0] : (data.errors.mobile || '');
          if (mobileErr.toLowerCase().includes('already')) setMobileAlreadyExists(true);
        } else if (data.message) {
          setErrors({ general: data.message });
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Social login handlers
  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/auth/google`;
  };

  const handleFacebookLogin = () => {
    window.location.href = `${API_URL}/auth/facebook`;
  };

  
  const getPasswordStrengthLabel = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Weak password';
      case 2:
        return 'Fair password';
      case 3:
        return 'Good password';
      case 4:
        return 'Strong password';
      default:
        return '';
    }
  };

  const getPasswordStrengthClass = () => {
    if (passwordStrength <= 1) return 'weak';
    if (passwordStrength === 2) return 'medium';
    return 'strong';
  };

  if (!isOpen) return null;

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <div className="logo">A</div>
          <h1>Create Account</h1>
          <p className="subtitle">Join thousands of users already on board</p>
        </div>

        {successMessage && (
          <div className="success-message">
            <span>✅</span>
            <span>{successMessage}</span>
          </div>
        )}

        {errors.general && (
          <div className="error-message-general">
            <span>⚠️</span>
            <span>{errors.general}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Full Name */}
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <div className="input-wrapper">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your full name here"
                className={errors.name && touched.name ? 'error' : (touched.name && !errors.name ? 'success' : '')}
                autoComplete="name"
              />
            </div>
            {errors.name && touched.name && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.name}</span>
              </div>
            )}
          </div>

          {/* Mobile Number */}
          <div className="form-group">
            <label htmlFor="mobile">Mobile Number</label>
            <div className="input-wrapper">
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your mobile number"
                className={errors.mobile && touched.mobile ? 'error' : (touched.mobile && !errors.mobile ? 'success' : '')}
                autoComplete="tel"
              />
            </div>
            {errors.mobile && touched.mobile && !mobileAlreadyExists && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.mobile}</span>
              </div>
            )}
            {mobileAlreadyExists && (
              <div className="rounded-2xl overflow-hidden border border-orange-200 mt-2">
                <div className="flex items-start gap-2.5 px-4 py-3 bg-orange-50">
                  <span className="text-orange-500 mt-0.5">⚠️</span>
                  <span className="text-sm font-medium text-orange-700">This mobile number is already registered.</span>
                </div>
                <div className="px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white text-xs font-bold leading-tight">Already have an account?</p>
                    <p className="text-white/75 text-[11px]">Sign in with this number instead</p>
                  </div>
                  <button
                    type="button"
                    onClick={onBackToLogin}
                    className="flex-shrink-0 bg-white text-orange-600 text-xs font-bold px-4 py-2 rounded-xl shadow-sm hover:shadow-md active:scale-95 transition-all whitespace-nowrap"
                  >
                    Sign In →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Your email id here"
                className={errors.email && touched.email ? 'error' : (touched.email && !errors.email ? 'success' : '')}
                autoComplete="email"
              />
            </div>
            {errors.email && touched.email && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Create a strong password"
                className={errors.password && touched.password ? 'error' : (touched.password && !errors.password ? 'success' : '')}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {errors.password && touched.password && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.password}</span>
              </div>
            )}
            {formData.password && (
              <div className="password-strength">
                <div className="strength-bars">
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      className={`strength-bar ${level <= passwordStrength ? `active ${getPasswordStrengthClass()}` : ''}`}
                    />
                  ))}
                </div>
                <div className="strength-text">{getPasswordStrengthLabel()}</div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="password_confirmation">Confirm Password</label>
            <div className="input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="password_confirmation"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Re-enter your password"
                className={errors.password_confirmation && touched.password_confirmation ? 'error' : (touched.password_confirmation && !errors.password_confirmation ? 'success' : '')}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label="Toggle password visibility"
              >
                {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {errors.password_confirmation && touched.password_confirmation && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.password_confirmation}</span>
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="form-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="terms"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <label htmlFor="terms" className="checkbox-label">
                I agree to the <a href="/terms">Terms of Service</a> and{' '}
                <a href="/privacy">Privacy Policy</a>
              </label>
            </div>
            {errors.terms && touched.terms && (
              <div className="field-error">
                <span>⚠️</span>
                <span>{errors.terms}</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button type="submit" className="submit-btn" disabled={isLoading || !formData.terms} style={!formData.terms && !isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Creating account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Social Login */}
       
        <div className="divider">
          <span>Or continue with</span>
        </div>
 {socialMediaLoginEnabled && (
        <div className="social-login">
          <button type="button" className="social-btn" onClick={handleGoogleLogin}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Google
          </button>
          <button type="button" className="social-btn facebook-btn" onClick={handleFacebookLogin}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </button>
         
        </div>
        )}

        {/* Footer */}
        <div className="footer">
          Already have an account? <button type="button" onClick={onBackToLogin} className="link-button">Sign in</button>
        </div>
        {onShowAbout && (
          <p className="text-xs text-center text-gray-400 mt-3">
            <button onClick={onShowAbout} className="text-blue-500 hover:underline">About Us</button>
          </p>
        )}
      </div>
    </div>
  );
};

export default SignUpModal;
